import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Flask, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Section, Field, ErrorBox, Empty, RevisionSelect, SourceTag, StatusPill, VoidForm, inputCls, errMsg, fmtDate } from "./shared";

const today = () => new Date().toISOString().slice(0, 10);
const blank = (revId) => ({ revision_id: revId, name: "", value: "", result_text: "", unit: "", method: "", tested_at: today(), notes: "" });

function specText(spec) {
  if (!spec) return "No target";
  if (typeof spec === "string") return spec;
  const parts = [];
  if (spec.min != null) parts.push(`≥ ${spec.min}`);
  if (spec.max != null) parts.push(`≤ ${spec.max}`);
  if (spec.expected) parts.push(spec.expected);
  return `${parts.join(", ") || "—"}${spec.unit ? ` ${spec.unit}` : ""}`;
}

export default function TestsPanel({ recipeId, recipe, revisions, tests, targets, onReload }) {
  const [filter, setFilter] = useState("");
  const [showVoided, setShowVoided] = useState(true);
  const [form, setForm] = useState(() => blank(recipe.current_revision_id || revisions[0]?.id || ""));
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [voiding, setVoiding] = useState(null);
  const [voidBusy, setVoidBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickTarget = (name) => {
    const t = targets.find((x) => x.name === name);
    setForm((f) => ({ ...f, name, unit: t?.unit ?? f.unit }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/rd/recipes/${recipeId}/tests`, {
        ...form,
        value: form.value === "" ? null : Number(form.value),
      });
      toast.success("Test result recorded");
      setForm(blank(form.revision_id)); setOpen(false);
      onReload();
    } catch (err) { setError(`${errMsg(err)} Your entry is preserved.`); }
    finally { setSaving(false); }
  };

  const doVoid = async (t, reason) => {
    setVoidBusy(true);
    try {
      await api.post(`/rd/recipes/${recipeId}/tests/${t.id}/void`, { reason });
      toast.success("Record voided — it stays in history");
      setVoiding(null); onReload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setVoidBusy(false); }
  };

  const correct = (t) => {
    setForm({ revision_id: t.revision_id, name: t.name, value: t.value ?? "", result_text: t.result_text || "", unit: t.unit || "", method: t.method || "", tested_at: today(), notes: `Correction of record ${t.id}` });
    setOpen(true);
  };

  const list = tests.filter((t) => (!filter || t.revision_id === filter) && (showVoided || !t.voided));
  const noRev = revisions.length === 0;

  return (
    <Section icon={Flask} title="Analytical testing" testId="rd-tests"
      subtitle="Lab records are append-only. Pass/fail is assessed server-side against brief targets; records with no matching target stay unassessed."
      actions={<Button size="sm" disabled={noRev} onClick={() => setOpen((o) => !o)} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="button-toggle-test-form"><Plus size={13} className="mr-1" /> Record result</Button>}>
      {noRev && <Empty>Save a formula revision first — every result must be tied to the exact revision tested.</Empty>}

      {open && !noRev && (
        <form onSubmit={submit} className="rounded-md border border-stone-200 bg-stone-50/60 p-4 mb-4 space-y-3" data-testid="form-test">
          <ErrorBox message={error} testId="error-test" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Revision tested"><RevisionSelect revisions={revisions} value={form.revision_id} onChange={(v) => set("revision_id", v)} testId="select-test-revision" /></Field>
            <Field label="Test name" hint={targets.length ? "Pick a brief target so the result can be assessed." : "No brief targets defined — result will be unassessed."}>
              {targets.length ? (
                <select required className={inputCls} value={form.name} onChange={(e) => pickTarget(e.target.value)} data-testid="select-test-name">
                  <option value="">Select target…</option>
                  {targets.map((t) => <option key={t.name} value={t.name}>{t.name}{t.unit ? ` (${t.unit})` : ""}</option>)}
                  {form.name && !targets.some((t) => t.name === form.name) && <option value={form.name}>{form.name}</option>}
                </select>
              ) : (
                <input required className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="input-test-name" />
              )}
            </Field>
            <Field label="Numeric value"><input type="number" step="any" className={inputCls} value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="input-test-value" /></Field>
            <Field label="Unit"><input className={inputCls} value={form.unit} onChange={(e) => set("unit", e.target.value)} data-testid="input-test-unit" /></Field>
            <Field label="Result text (non-numeric)"><input className={inputCls} value={form.result_text} onChange={(e) => set("result_text", e.target.value)} data-testid="input-test-result-text" /></Field>
            <Field label="Method"><input className={inputCls} placeholder="e.g. AOAC 981.12, calibrated pH meter" value={form.method} onChange={(e) => set("method", e.target.value)} data-testid="input-test-method" /></Field>
            <Field label="Tested on"><input type="date" required className={inputCls} value={form.tested_at} onChange={(e) => set("tested_at", e.target.value)} data-testid="input-test-date" /></Field>
            <Field label="Notes"><input className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-test-notes" /></Field>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name || (form.value === "" && !form.result_text.trim())} data-testid="button-submit-test">{saving ? "Saving…" : "Save record"}</Button>
          </div>
        </form>
      )}

      {!noRev && (
        <div className="flex gap-3 items-center flex-wrap mb-3">
          <div className="w-full sm:w-80"><RevisionSelect allowAll revisions={revisions} value={filter} onChange={setFilter} testId="select-test-filter" /></div>
          <label className="text-xs text-stone-600 flex items-center gap-1.5">
            <input type="checkbox" checked={showVoided} onChange={(e) => setShowVoided(e.target.checked)} data-testid="checkbox-show-voided" /> Show voided
          </label>
        </div>
      )}

      {!noRev && list.length === 0 && <Empty>No analytical records{filter ? " for this revision" : ""} yet.</Empty>}
      <div className="space-y-2">
        {list.map((t) => (
          <div key={t.id} className={`rounded-md border px-3 py-2.5 ${t.voided ? "border-stone-200 bg-stone-50 opacity-70" : "border-stone-200 bg-white"}`} data-testid={`row-test-${t.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold text-stone-800 ${t.voided ? "line-through" : ""}`}>{t.name}</span>
                  <StatusPill status={t.voided ? "voided" : t.status} />
                  <SourceTag revisions={revisions} revisionId={t.revision_id} currentId={recipe.current_revision_id} />
                </div>
                <div className="text-sm text-stone-700 mt-0.5 font-mono">
                  {t.value != null ? `${t.value}${t.unit ? ` ${t.unit}` : ""}` : t.result_text || "—"}
                  {t.value != null && t.result_text && <span className="font-sans text-stone-500"> · {t.result_text}</span>}
                  <span className="font-sans text-xs text-stone-500 ml-2">Spec: {specText(t.spec)}</span>
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {t.method && <>Method: {t.method} · </>}Tested {t.tested_at} · Recorded {fmtDate(t.created_at)}{t.recorded_by ? ` by ${t.recorded_by}` : ""}
                </div>
                {t.notes && <div className="text-xs text-stone-600 mt-0.5">{t.notes}</div>}
                {t.voided && <div className="text-xs text-red-700 mt-0.5">Voided: {t.void_reason}</div>}
              </div>
              {!t.voided && voiding !== t.id && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setVoiding(t.id)} data-testid={`button-void-test-${t.id}`}>Void</Button>
                </div>
              )}
              {t.voided && <Button size="sm" variant="ghost" onClick={() => correct(t)} data-testid={`button-correct-test-${t.id}`}>Enter correction</Button>}
            </div>
            {voiding === t.id && <VoidForm busy={voidBusy} onCancel={() => setVoiding(null)} onSubmit={(r) => doVoid(t, r)} />}
          </div>
        ))}
      </div>
    </Section>
  );
}
